import { ArrowRight, Scale, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { FALLBACK_SITE_CONTENT, normalizeSiteContent } from "../utils/siteContent";

export default function HomePage({ content }) {
  const safeContent = normalizeSiteContent(content);
  const marqueeItems =
    safeContent.marquee_items.length > 0
      ? [...safeContent.marquee_items, ...safeContent.marquee_items]
      : FALLBACK_SITE_CONTENT.marquee_items;

  return (
    <div data-testid="home-page">
      <section className="page-shell section-gap grid-12 items-end" data-testid="home-hero-section">
        <div className="space-y-8 md:col-span-7 lg:col-span-8">
          <p className="eyebrow" data-testid="hero-overline">
            {safeContent.hero_overline}
          </p>
          <h1 className="headline-xl max-w-5xl" data-testid="hero-title">
            {safeContent.hero_title}
          </h1>
          <div className="max-w-3xl space-y-4">
            <p className="text-base font-semibold uppercase tracking-[0.16em] text-slate-700 md:text-lg" data-testid="hero-subtitle">
              {safeContent.hero_subtitle}
            </p>
            <p className="body-copy" data-testid="hero-description">
              {safeContent.hero_description}
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link className="solid-button" data-testid="hero-primary-cta" to="/contact">
              Arrange a consultation
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link className="outline-button" data-testid="hero-secondary-cta" to="/services">
              Explore practice areas
            </Link>
          </div>
        </div>

        <div className="md:col-span-5 lg:col-span-4" data-testid="hero-image-column">
          <div className="relative overflow-hidden border border-slate-900/10 bg-white p-4 shadow-[16px_16px_0_rgba(15,23,42,0.08)]">
            <div className="absolute left-4 top-4 z-10 max-w-[220px] border border-white/40 bg-slate-950/80 p-4 text-white backdrop-blur" data-testid="hero-highlight-card">
              <p className="text-3xl font-black uppercase text-amber-400">UK + EU</p>
              <p className="mt-2 text-xs uppercase tracking-[0.28em] text-slate-300">Wins built on meticulous preparation</p>
            </div>
            <img
              alt="Peters Van Kort"
              className="aspect-[4/5] w-full object-cover object-center transition-transform duration-500 hover:scale-[1.02]"
              data-testid="hero-image"
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = FALLBACK_SITE_CONTENT.architecture_image;
              }}
              src={safeContent.hero_image}
            />
          </div>
        </div>
      </section>

      <section className="marquee-shell" data-testid="practice-marquee-section">
        <div className="marquee-track">
          {marqueeItems.map((item, index) => (
            <span className="marquee-item" data-testid={`marquee-item-${index + 1}`} key={`${item}-${index}`}>
              {item}
            </span>
          ))}
        </div>
      </section>

      <section className="page-shell section-gap" data-testid="home-profile-section">
        <div className="grid gap-10 md:grid-cols-[1.1fr_0.9fr] md:items-start">
          <div className="space-y-6">
            <p className="eyebrow" data-testid="profile-eyebrow">
              Professional profile
            </p>
            <h2 className="headline-lg max-w-3xl" data-testid="profile-heading">
              A premium legal profile built on experience, discipline, and client trust.
            </h2>
            {safeContent.profile_summary.map((paragraph, index) => (
              <p className="body-copy max-w-3xl" data-testid={`profile-summary-${index + 1}`} key={paragraph}>
                {paragraph}
              </p>
            ))}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="panel-card bg-slate-950 text-white" data-testid="profile-value-card-strategy">
              <Scale className="h-10 w-10 text-amber-400" />
              <h3 className="mt-5 text-2xl font-black uppercase tracking-tight">Strategic depth</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                Civil disputes, immigration matters, and cross-border claims handled with clear judgment and strong preparation.
              </p>
            </div>
            <div className="panel-card" data-testid="profile-value-card-trust">
              <ShieldCheck className="h-10 w-10 text-amber-700" />
              <h3 className="mt-5 text-2xl font-black uppercase tracking-tight text-slate-950">Client confidence</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                Respectful guidance, clear communication, and a practical path toward resolution or trial.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-slate-950 text-white" data-testid="home-stats-section">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "url(https://images.pexels.com/photos/3137086/pexels-photo-3137086.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)", backgroundPosition: "center", backgroundSize: "cover" }} />
        <div className="page-shell section-gap relative">
          <div className="max-w-3xl space-y-4">
            <p className="eyebrow text-amber-400" data-testid="stats-eyebrow">
              Track record
            </p>
            <h2 className="headline-lg text-white" data-testid="stats-heading">
              The numbers and milestones behind a confident legal presence.
            </h2>
          </div>

          <div className="mt-12 grid gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-2 xl:grid-cols-4" data-testid="stats-grid">
            {safeContent.stats.map((stat, index) => (
              <article className="bg-slate-950/80 p-8 backdrop-blur-sm" data-testid={`stats-card-${index + 1}`} key={stat.label}>
                <p className="text-5xl font-black uppercase tracking-tighter text-white md:text-6xl" data-testid={`stats-value-${index + 1}`}>
                  {stat.value}
                </p>
                <p className="mt-4 text-sm font-bold uppercase tracking-[0.2em] text-amber-400" data-testid={`stats-label-${index + 1}`}>
                  {stat.label}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-slate-300" data-testid={`stats-detail-${index + 1}`}>
                  {stat.detail}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="page-shell section-gap" data-testid="home-services-preview-section">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div className="space-y-4">
            <p className="eyebrow" data-testid="services-preview-eyebrow">
              Practice areas
            </p>
            <h2 className="headline-lg max-w-3xl" data-testid="services-preview-heading">
              Built for disputes that need both force and precision.
            </h2>
          </div>
          <Link className="outline-button" data-testid="services-preview-link" to="/services">
            View all services
          </Link>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          {safeContent.services.map((service, index) => (
            <article className="panel-card" data-testid={`service-preview-card-${index + 1}`} key={service.title}>
              <p className="text-xs font-bold uppercase tracking-[0.26em] text-amber-700" data-testid={`service-preview-highlight-${index + 1}`}>
                {service.highlight}
              </p>
              <h3 className="mt-5 text-3xl font-black uppercase tracking-tight text-slate-950" data-testid={`service-preview-title-${index + 1}`}>
                {service.title}
              </h3>
              <p className="mt-4 text-base leading-relaxed text-slate-600" data-testid={`service-preview-description-${index + 1}`}>
                {service.description}
              </p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}